/**
 * leadStatusLabel.ts
 *
 * Maps lead `status` values to i18n key paths and visual tone.
 * Usage:
 *   const { labelKey, tone } = getLeadStatusMeta(lead.status);
 *   const label = t(labelKey);
 */

import type { LeadStatus } from '@evo-hub/shared';

export type LeadStatusTone = 'neutral' | 'info' | 'success' | 'warn' | 'danger' | 'muted';

export interface LeadStatusMeta {
  labelKey: string;
  tone: LeadStatusTone;
}

const STATUS_MAP: Record<LeadStatus, LeadStatusMeta> = {
  pending:      { labelKey: 'leads.statusLabels.pending',      tone: 'neutral' },
  scraped:      { labelKey: 'leads.statusLabels.scraped',      tone: 'neutral' },
  enriched:     { labelKey: 'leads.statusLabels.enriched',     tone: 'info'    },
  analyzed:     { labelKey: 'leads.statusLabels.analyzed',     tone: 'info'    },
  drafted:      { labelKey: 'leads.statusLabels.drafted',      tone: 'info'    },
  sent:         { labelKey: 'leads.statusLabels.sent',         tone: 'success' },
  replied:      { labelKey: 'leads.statusLabels.replied',      tone: 'success' },
  closed_won:   { labelKey: 'leads.statusLabels.closed_won',   tone: 'success' },
  closed_lost:  { labelKey: 'leads.statusLabels.closed_lost',  tone: 'warn'    },
  archived:     { labelKey: 'leads.statusLabels.archived',     tone: 'muted'   },
  disqualified: { labelKey: 'leads.statusLabels.disqualified', tone: 'danger'  },
  junk:         { labelKey: 'leads.statusLabels.junk',         tone: 'danger'  },
  deleted:      { labelKey: 'leads.statusLabels.deleted',      tone: 'muted'   },
  manual:       { labelKey: 'leads.statusLabels.manual',       tone: 'neutral' },
  failed:       { labelKey: 'leads.statusLabels.failed',       tone: 'danger'  },
};

const FALLBACK: LeadStatusMeta = { labelKey: 'leads.statusLabels.unknown', tone: 'neutral' };

export function getLeadStatusMeta(status: string): LeadStatusMeta {
  return STATUS_MAP[status as LeadStatus] ?? FALLBACK;
}

/**
 * Returns the appropriate stage value to display in UI badges.
 *
 * `lead.status` is the persistence/filtering state (scraped/enriched/disqualified/archived/junk).
 * `lead.pipelineStage` is the outreach lifecycle (new → analyzed → drafted → sent → closed).
 *
 * Special states (disqualified, archived, junk, deleted) take priority because they reflect
 * a terminal qualification result that overrides the outreach lifecycle. Otherwise the
 * pipeline stage is the meaningful indicator of progress.
 */
export function getDisplayStage(lead: { status: string; pipelineStage: string }): string {
  const TERMINAL_STATUS = new Set<LeadStatus>(['disqualified', 'archived', 'junk', 'deleted']);
  return TERMINAL_STATUS.has(lead.status as LeadStatus) ? lead.status : lead.pipelineStage;
}

export function parseDisqualifyReason(enrichmentError: string | null): string {
  if (enrichmentError?.startsWith('Disqualified: ')) {
    return enrichmentError.slice('Disqualified: '.length);
  }
  return 'unknown';
}

export interface TerminalReasonView {
  /** i18n key for the reason label — pass to t() in the consumer */
  labelKey: string;
  /** Optional fallback key if labelKey has no translation — pass as defaultValue */
  fallbackKey?: string;
  colorVar: string;
}

export function getTerminalReason(
  lead: { status: string; junkReason: string | null; enrichmentError: string | null },
): TerminalReasonView | null {
  if (lead.status === 'disqualified') {
    const reason = parseDisqualifyReason(lead.enrichmentError);
    return {
      labelKey: `leads.qualifyReasons.${reason}`,
      fallbackKey: 'leads.statusLabels.disqualified',
      colorVar: '--evo-danger',
    };
  }
  if (lead.status === 'junk') {
    return {
      labelKey: `leads.junkReasons.${lead.junkReason ?? 'unknown'}`,
      fallbackKey: 'leads.statusLabels.junk',
      colorVar: '--evo-danger',
    };
  }
  if (lead.status === 'archived') {
    if (lead.junkReason === 'auto_closed_no_response') {
      return { labelKey: 'leads.junkReasons.auto_closed_no_response', colorVar: '--evo-fg-3' };
    }
    return { labelKey: 'leads.statusLabels.archived', colorVar: '--evo-fg-3' };
  }
  return null;
}
